import type { HierarcicalObjectReference } from "@novorender/data-js-api";
import { useCallback, useContext, useMemo, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useDispatchHidden, useHidden } from "contexts/hidden";
import { actions as hiddenActions } from "contexts/hidden/reducer";
import {
    actions as collectionActions,
    DispatchContext,
    HighlightCollection,
    StateContext,
} from "contexts/highlightCollections/reducer";
import type { ObjectDataAccess } from "dataAccess";
import { renderActions, selectPlainIsolationActive } from "features/render/renderSlice";
import { undoRedoActions } from "features/undoRedo";
import * as objectActions from "utils/objectActions";
import { type IdList, SelectionSet } from "utils/selectionSet";
import type { GroupState } from "utils/treeHelpers";
import { restoreUndoIds, snapshotUndoIds } from "utils/undoIds";

const noopDispatch = () => {};

export function invertGroupState(state: GroupState): GroupState {
    return state === "all" ? "none" : state === "none" ? "all" : "some";
}

/**
 * `keepIds.difference(hiddenIds)`, shared across callers by operand identity.
 *
 * This hook runs in every rendered tree row and the diff is O(keepIds.size) — scene-sized
 * after isolating and then showing everything (measured at ~15ms per row). The cache has
 * to live outside the hook: rows remount rather than update, so the `useMemo` below is
 * discarded before it can pay off. Sharing one instance also keeps the WeakMap counters in
 * treeHelpers hitting, instead of rebuilding for each row's private result.
 *
 * Both operands are immutable and replaced rather than mutated, so identity is a sound key.
 */
const visibleIdsByKeepIds = new WeakMap<SelectionSet, WeakMap<SelectionSet, SelectionSet>>();

function getVisibleIds(keepIds: SelectionSet, hiddenIds: SelectionSet): SelectionSet {
    let byHiddenIds = visibleIdsByKeepIds.get(keepIds);
    if (!byHiddenIds) {
        byHiddenIds = new WeakMap();
        visibleIdsByKeepIds.set(keepIds, byHiddenIds);
    }

    const cached = byHiddenIds.get(hiddenIds);
    if (cached) {
        return cached;
    }

    const visibleIds = keepIds.difference(hiddenIds);
    byHiddenIds.set(hiddenIds, visibleIds);
    return visibleIds;
}

export function useIsolationVisibility(): {
    active: boolean;
    visibleIds: SelectionSet | undefined;
    keepIds: SelectionSet | undefined;
    setIdsVisible: (ids: IdList, visible: boolean) => void;
    setHierarchyVisible: (params: {
        nodes: HierarcicalObjectReference[];
        visible: boolean;
        dataAccess: ObjectDataAccess;
        abortSignal: AbortSignal;
        setLoading: (value: boolean) => void;
        filteredObjectIds?: Set<number> | null;
    }) => Promise<number[]>;
    /** Bulk variant — parallelizes all descendant fetches and dispatches a single batch. */
    setHierarchyVisibleBulk: (params: {
        nodes: HierarcicalObjectReference[];
        visible: boolean;
        dataAccess: ObjectDataAccess;
        abortSignal: AbortSignal;
        setLoading: (value: boolean) => void;
        filteredObjectIds?: Set<number> | null;
    }) => Promise<number[]>;
} {
    const active = useAppSelector(selectPlainIsolationActive);
    const dispatch = useAppDispatch();
    const hidden = useHidden();
    const dispatchHidden = useDispatchHidden();
    const collections = useContext(StateContext);
    const dispatchCollection = useContext(DispatchContext) ?? noopDispatch;
    const keepIds = collections?.[HighlightCollection.TransparentSelectionBase].ids;
    const visibleIds = useMemo(
        () => (active && keepIds ? getVisibleIds(keepIds, hidden.ids) : undefined),
        [active, hidden.ids, keepIds],
    );
    const hiddenIdsRef = useRef(hidden.ids);
    hiddenIdsRef.current = hidden.ids;
    const keepIdsRef = useRef(keepIds ?? SelectionSet.EMPTY);
    if (keepIds) {
        keepIdsRef.current = keepIds;
    }
    const activeRef = useRef(active);
    activeRef.current = active;

    const persistKeepSet = useCallback(
        (next: SelectionSet) => {
            keepIdsRef.current = next;
            if (activeRef.current) {
                dispatch(renderActions.setPlainIsolationSnapshotIds(Array.from(next)));
            }
        },
        [dispatch],
    );

    const applyVisibility = useCallback(
        (ids: IdList, visible: boolean) => {
            dispatchCollection(
                visible
                    ? collectionActions.add(HighlightCollection.TransparentSelectionBase, ids)
                    : collectionActions.remove(HighlightCollection.TransparentSelectionBase, ids),
            );
            if (visible) {
                dispatchHidden(hiddenActions.remove(ids));
            }
            persistKeepSet(visible ? keepIdsRef.current.withAdded(ids) : keepIdsRef.current.withRemoved(ids));
        },
        [dispatchCollection, dispatchHidden, persistKeepSet],
    );

    // Undo only the keep-set / hidden membership that actually changed. Showing an
    // already-kept id (closed-eye because it is also in `hidden`) must not drop it
    // from the keep-set; hiding an id that was already outside must not add it.
    const pushUndo = useCallback(
        (ids: IdList, visible: boolean, hiddenBefore: SelectionSet, keepBefore: SelectionSet) => {
            if (ids.length === 0) {
                return;
            }
            const idArr = Array.from(ids);
            const addedToKeep = visible ? idArr.filter((id) => !keepBefore.has(id)) : [];
            const removedFromKeep = visible ? [] : idArr.filter((id) => keepBefore.has(id));
            const unhidden = visible ? idArr.filter((id) => hiddenBefore.has(id)) : [];
            if (addedToKeep.length === 0 && removedFromKeep.length === 0 && unhidden.length === 0) {
                return;
            }

            const shrunkAdded = addedToKeep.length > 0 ? snapshotUndoIds(addedToKeep) : undefined;
            const shrunkRemoved = removedFromKeep.length > 0 ? snapshotUndoIds(removedFromKeep) : undefined;
            const shrunkUnhidden = unhidden.length > 0 ? snapshotUndoIds(unhidden) : undefined;

            dispatch(
                undoRedoActions.push({
                    description: visible ? "Show objects" : "Hide objects",
                    undoCallback: () => {
                        if (shrunkAdded) {
                            dispatchCollection(
                                collectionActions.remove(
                                    HighlightCollection.TransparentSelectionBase,
                                    restoreUndoIds(shrunkAdded),
                                ),
                            );
                        }
                        if (shrunkRemoved) {
                            dispatchCollection(
                                collectionActions.add(
                                    HighlightCollection.TransparentSelectionBase,
                                    restoreUndoIds(shrunkRemoved),
                                ),
                            );
                        }
                        if (shrunkUnhidden) {
                            dispatchHidden(hiddenActions.add(restoreUndoIds(shrunkUnhidden)));
                        }
                        persistKeepSet(keepIdsRef.current.withRemoved(addedToKeep).withAdded(removedFromKeep));
                    },
                    redoCallback: () => applyVisibility(idArr, visible),
                }),
            );
        },
        [applyVisibility, dispatch, dispatchCollection, dispatchHidden, persistKeepSet],
    );

    const setIdsVisible = useCallback(
        (ids: IdList, visible: boolean) => {
            const keepBefore = keepIdsRef.current;
            pushUndo(ids, visible, hiddenIdsRef.current, keepBefore);
            applyVisibility(ids, visible);
        },
        [applyVisibility, pushUndo],
    );

    const setHierarchyVisible = useCallback(
        async (
            params: Omit<
                Parameters<typeof objectActions.setObjectHierarchyIsolationVisibility>[0],
                "dispatchHidden" | "dispatchHighlightCollection"
            >,
        ) => {
            const hiddenBefore = hiddenIdsRef.current;
            const keepBefore = keepIdsRef.current;
            const affectedIds = await objectActions.setObjectHierarchyIsolationVisibility({
                ...params,
                dispatchHidden,
                dispatchHighlightCollection: dispatchCollection,
            });
            if (affectedIds.length > 0) {
                persistKeepSet(
                    params.visible ? keepBefore.withAdded(affectedIds) : keepBefore.withRemoved(affectedIds),
                );
                pushUndo(affectedIds, params.visible, hiddenBefore, keepBefore);
            }
            return affectedIds;
        },
        [dispatchCollection, dispatchHidden, persistKeepSet, pushUndo],
    );

    const setHierarchyVisibleBulk = useCallback(
        async (
            params: Omit<
                Parameters<typeof objectActions.bulkSetAllIsolationVisibilityHierarchies>[0],
                "dispatchHidden" | "dispatchHighlightCollection"
            >,
        ) => {
            const hiddenBefore = hiddenIdsRef.current;
            const keepBefore = keepIdsRef.current;
            const affectedIds = await objectActions.bulkSetAllIsolationVisibilityHierarchies({
                ...params,
                dispatchHidden,
                dispatchHighlightCollection: dispatchCollection,
            });
            if (affectedIds.length > 0) {
                persistKeepSet(
                    params.visible ? keepBefore.withAdded(affectedIds) : keepBefore.withRemoved(affectedIds),
                );
                pushUndo(affectedIds, params.visible, hiddenBefore, keepBefore);
            }
            return affectedIds;
        },
        [dispatchCollection, dispatchHidden, persistKeepSet, pushUndo],
    );

    return useMemo(
        () => ({ active, visibleIds, keepIds, setIdsVisible, setHierarchyVisible, setHierarchyVisibleBulk }),
        [active, keepIds, setHierarchyVisible, setHierarchyVisibleBulk, setIdsVisible, visibleIds],
    );
}
